import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { OrganisationContext } from '../../../../context';
import {
  organisationApi,
  OrganisationTwoFactorForce,
  SessionExpirationTime,
} from '../../../../services';
import { Button, ButtonType, Check, Select } from '../../../../shared';

import classes from './Security.module.scss';

export const Security: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardSecurity' });

  const [organisationUpdate] = organisationApi.endpoints.organisationUpdate.useMutation();

  const { organisation, setOrganisation } = useContext(OrganisationContext);

  const {
    twoFactorForce: orgTwoFactorForce,
    sessionExpirationTime: orgSessionExpirationTime,
    sessionExpirationTimeApp: orgSessionExpirationTimeApp,
    appLockEnabled: orgAppLockEnabled,
  } = organisation;

  const [twoFactorForce, setTwoFactorForce] =
    useState<OrganisationTwoFactorForce>(orgTwoFactorForce);

  const [sessionExpirationTime, setSessionExpirationTime] =
    useState<SessionExpirationTime>(orgSessionExpirationTime);

  const [sessionExpirationTimeApp, setSessionExpirationTimeApp] = useState<SessionExpirationTime>(
    orgSessionExpirationTimeApp
  );

  const [appLockEnabled, setAppLockEnabled] = useState<boolean>(orgAppLockEnabled);

  const [loading, setLoading] = useState<boolean>(false);

  const twoFactorForceItems = useMemo(() => {
    return Object.values(OrganisationTwoFactorForce).map((value) => {
      return { id: value, title: t(`twoFactorForceType.${value}`) };
    });
  }, [t]);

  const sessionExpirationItems = useMemo(() => {
    return Object.values(SessionExpirationTime).map((value) => {
      return { id: value, title: t(`sessionExpiration.${value}`) };
    });
  }, [t]);

  const onSave = useCallback(async () => {
    setLoading(true);

    try {
      const payload = await toast.promise(
        organisationUpdate({
          fields: {
            twoFactorForce,
            sessionExpirationTime,
            sessionExpirationTimeApp,
            appLockEnabled,
          },
        }).unwrap(),
        { pending: t('orgDashboard.save-pending'), success: t('orgDashboard.save-success') }
      );

      if (payload) {
        setOrganisation?.({ ...organisation, ...payload });
        return;
      }
    } catch (_) {
      toast.error(t('orgDashboard.save-error'));
    } finally {
      setLoading(false);
    }
  }, [
    appLockEnabled,
    organisation,
    organisationUpdate,
    sessionExpirationTime,
    sessionExpirationTimeApp,
    setOrganisation,
    t,
    twoFactorForce,
  ]);

  return (
    <div className={classes['security']}>
      <Select
        label={ctxT('enable-2fa')}
        selectedItemId={twoFactorForce}
        items={twoFactorForceItems}
        onChange={(type) => setTwoFactorForce(type as OrganisationTwoFactorForce)}
      />

      <Select
        label={ctxT('session-expiration-time')}
        selectedItemId={sessionExpirationTime}
        items={sessionExpirationItems}
        onChange={(type) => setSessionExpirationTime(type as SessionExpirationTime)}
      />

      <Select
        label={ctxT('session-expiration-time-app')}
        selectedItemId={sessionExpirationTimeApp}
        items={sessionExpirationItems}
        onChange={(type) => setSessionExpirationTimeApp(type as SessionExpirationTime)}
      />

      <Check
        checked={appLockEnabled}
        onChange={setAppLockEnabled}
        label={ctxT('allow-app-access-when-secured')}
      />

      <Button
        type={ButtonType.primary}
        label={t('common.save')}
        onClick={onSave}
        disabled={loading}
      />
    </div>
  );
};
